/**
 * ConfirmPH: Review invite card (BookingDetail).
 *
 * Shown for completed bookings. Lets staff:
 *   • See whether an invite was sent / used.
 *   • Copy the secure review link (rotates each time).
 *   • Resend the invite email to the customer.
 *
 * The raw token is only available immediately after a `send` / `resend`
 *, once the dashboard re-loads we only show the masked summary so the
 * link can't be leaked from the dashboard payload.
 */
import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Mail, Copy, RefreshCw, CheckCircle2, ShieldCheck, Clock, Loader2, ExternalLink,
} from "lucide-react";

export default function ReviewInviteCard({ booking }) {
  const completed = booking?.status === "completed";
  const [state, setState] = useState({ loading: true, invite: null, review: null });
  const [token, setToken] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!completed) {
      setState({ loading: false, invite: null, review: null });
      return;
    }
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get(`/bookings/${booking.id}/review-invite`);
      setState({ loading: false, invite: data.invite, review: data.review });
    } catch (_) {
      setState({ loading: false, invite: null, review: null });
    }
  }, [booking?.id, completed]);

  useEffect(() => {
    let cancelled = false;
    (async () => { if (!cancelled) await load(); })();
    return () => { cancelled = true; };
  }, [load]);

  const send = async (mode) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${booking.id}/review-invite/send`);
      if (data.token) setToken({ value: data.token, url: data.review_url });
      toast.success(mode === "resend"
        ? "Review link rotated and emailed to the customer"
        : "Review invite sent to the customer");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!token?.url) return;
    try {
      await navigator.clipboard.writeText(token.url);
      toast.success("Review link copied");
    } catch {
      toast.error("Could not copy: please copy manually");
    }
  };

  if (!completed) return null;

  return (
    <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-5"
         data-testid="review-invite-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-emerald-800">
            <ShieldCheck className="w-3.5 h-3.5" /> Review invite
          </div>
          <h3 className="font-display text-lg font-semibold mt-1">
            Invite this customer to leave a booking-linked review
          </h3>
          <p className="text-xs text-slate-600 mt-1 max-w-prose">
            ConfirmPH only publishes reviews tied to a real, completed booking.
            We auto-email the customer a secure link when you mark the booking
            completed. You can also copy or resend the link from here.
          </p>
        </div>
        {state.loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
      </div>

      {/* Already reviewed */}
      {state.review && (
        <div className="mt-3 rounded-lg border border-emerald-200 bg-white px-3 py-2.5 text-sm"
             data-testid="review-invite-already-reviewed">
          <div className="flex items-center gap-2 text-emerald-800 font-medium">
            <CheckCircle2 className="w-4 h-4" />
            Customer left a {state.review.rating}/5 review
            {state.review.status === "pending" && " (awaiting moderation)"}
            {state.review.status === "published" && " (published)"}
            {state.review.status === "hidden" && " (hidden)"}
            {state.review.status === "rejected" && " (rejected)"}
          </div>
          {state.review.title && (
            <div className="text-sm font-semibold mt-1.5">{state.review.title}</div>
          )}
          <p className="text-sm text-slate-700 mt-1 whitespace-pre-line">
            {state.review.comment}
          </p>
        </div>
      )}

      {/* Existing invite summary */}
      {!state.review && state.invite && (
        <div className="mt-3 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-xs text-slate-600"
             data-testid="review-invite-summary">
          <div className="flex items-center gap-2">
            <Mail className="w-3.5 h-3.5" />
            Invite sent to <span className="font-mono">{state.invite.customer_email_masked || "-"}</span>
            <Dot />
            <Clock className="w-3 h-3" />
            expires {state.invite.expires_at?.slice(0, 10) || "-"}
            <Dot />
            sent {state.invite.send_count}× · status: <span className="font-medium">{state.invite.status}</span>
          </div>
        </div>
      )}

      {/* Raw token (one-shot, only after send/resend) */}
      {token && (
        <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5"
             data-testid="review-invite-token-row">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-900 flex items-center gap-1.5">
            <ShieldCheck className="w-3 h-3" /> One-time link (won't show again)
          </div>
          <div className="mt-1.5 flex items-center gap-2 flex-wrap">
            <code className="text-xs bg-white border border-slate-200 px-2 py-1 rounded font-mono truncate max-w-full"
                  data-testid="review-invite-token-url">
              {token.url}
            </code>
            <button onClick={copy}
                    className="px-2.5 py-1 rounded-md border border-slate-300 bg-white text-xs hover:bg-slate-50 inline-flex items-center gap-1.5"
                    data-testid="review-invite-copy-btn">
              <Copy className="w-3 h-3" /> Copy
            </button>
            <a href={token.url} target="_blank" rel="noreferrer"
               className="px-2.5 py-1 rounded-md border border-slate-300 bg-white text-xs hover:bg-slate-50 inline-flex items-center gap-1.5"
               data-testid="review-invite-open-btn">
              <ExternalLink className="w-3 h-3" /> Open
            </a>
          </div>
        </div>
      )}

      {/* Actions */}
      {!state.review && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          {state.invite ? (
            <button onClick={() => send("resend")} disabled={busy}
                    className="px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm inline-flex items-center gap-2 disabled:opacity-60"
                    data-testid="review-invite-resend-btn">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <RefreshCw className="w-3.5 h-3.5" />}
              Resend & rotate link
            </button>
          ) : (
            <button onClick={() => send("send")} disabled={busy}
                    className="px-3 py-1.5 rounded-lg bg-emerald-700 text-white text-sm font-semibold hover:bg-emerald-800 inline-flex items-center gap-2 disabled:opacity-60"
                    data-testid="review-invite-send-btn">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <Mail className="w-3.5 h-3.5" />}
              Send review invite
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Dot() {
  return <span className="text-slate-300">·</span>;
}
